// src/redux/slices/messageCountSlice.js
import { createSlice } from '@reduxjs/toolkit';

const messageCountSlice = createSlice({
  name: 'messageCount',
  initialState: {
    totalCount: 0,
    isLoading: false,
    error: null,
    // Total unread count across all threads
    totalUnreadCount: 0,
    // Count per thread: { threadId: count }
    threadCounts: {},
    // Last message timestamp per thread for sorting
    lastMessageTimestamps: {},
    // Separate counts for sidebar display
    superAdminCount: 0,
    companyAdminCount: 0,
    // Role-based counts
    roleBased: {
      account_executive: 0,
      company_admin: 0,
      worker: 0,
      super_admin: 0,
    },
  },
  reducers: {
    // Update count - can increase or decrease based on action payload
    updateCount: (state, action) => {
      const { type } = action.payload;
      if (type === 'increment') {
        state.totalCount += 1;
      } else if (type === 'decrement') {
        state.totalCount = Math.max(0, state.totalCount - 1);
      }
      state.error = null;
    },

    // Set specific count value
    setCount: (state, action) => {
      state.totalCount = action.payload;
      state.error = null;
    },

    // Reset count to 0
    resetCount: (state) => {
      state.totalCount = 0;
      state.error = null;
    },

    // Set loading state
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },

    // Set error state
    setError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    // Initialize thread count (when thread is created or loaded)
    initializeThreadCount: (state, action) => {
      const { threadId, initialCount = 0 } = action.payload;
      
      state.threadCounts[threadId] = initialCount;
      state.lastMessageTimestamps[threadId] = new Date().toISOString();
      state.totalUnreadCount = Object.values(state.threadCounts).reduce((sum, count) => sum + count, 0);
    },

    // Increment count for a specific thread (when receiving a message)
    incrementThreadCount: (state, action) => {
      const { threadId, timestamp } = action.payload;
      
      if (!state.threadCounts[threadId]) {
        state.threadCounts[threadId] = 0;
      }
      
      state.threadCounts[threadId] += 1;
      state.lastMessageTimestamps[threadId] = timestamp || new Date().toISOString();
      state.totalUnreadCount = Object.values(state.threadCounts).reduce((sum, count) => sum + count, 0);
    },

    // Decrement count for a specific thread (when sending a message)
    decrementThreadCount: (state, action) => {
      const { threadId, timestamp } = action.payload;
      
      if (state.threadCounts[threadId] && state.threadCounts[threadId] > 0) {
        state.threadCounts[threadId] -= 1;
      }
      
      state.lastMessageTimestamps[threadId] = timestamp || new Date().toISOString();
      state.totalUnreadCount = Object.values(state.threadCounts).reduce((sum, count) => sum + count, 0);
    },

    // Clear count for a specific thread (when thread is opened/read)
    clearThreadCount: (state, action) => {
      const { threadId } = action.payload;
      
      if (state.threadCounts[threadId]) {
        state.threadCounts[threadId] = 0;
      }
      
      state.totalUnreadCount = Object.values(state.threadCounts).reduce((sum, count) => sum + count, 0);
    },

    // Clear all counts (when user logs out)
    clearAllCounts: (state) => {
      state.totalUnreadCount = 0;
      state.threadCounts = {};
      state.lastMessageTimestamps = {};
    },

    // Set count for a specific thread (when loading from server)
    setThreadCount: (state, action) => {
      const { threadId, count, timestamp } = action.payload;
      
      state.threadCounts[threadId] = count || 0;
      state.lastMessageTimestamps[threadId] = timestamp || new Date().toISOString();
      state.totalUnreadCount = Object.values(state.threadCounts).reduce((sum, count) => sum + count, 0);
    },

    // Update multiple thread counts (when loading threads from server)
    updateMultipleThreadCounts: (state, action) => {
      const { threadCounts } = action.payload;
      
      // Reset counts
      state.threadCounts = {};
      state.lastMessageTimestamps = {};
      
      // Set new counts
      Object.entries(threadCounts).forEach(([threadId, data]) => {
        state.threadCounts[threadId] = data.count || 0;
        state.lastMessageTimestamps[threadId] = data.timestamp || new Date().toISOString();
      });
      
      state.totalUnreadCount = Object.values(state.threadCounts).reduce((sum, count) => sum + count, 0);
    },

    // Get count for a specific thread
    getThreadCount: (state, action) => {
      const { threadId } = action.payload;
      return state.threadCounts[threadId] || 0;
    },

    // Update Super Admin count
    updateSuperAdminCount: (state, action) => {
      const { type } = action.payload;
      if (type === 'increment') {
        state.superAdminCount += 1;
      } else if (type === 'decrement') {
        state.superAdminCount = Math.max(0, state.superAdminCount - 1);
      } else if (type === 'set') {
        state.superAdminCount = action.payload.count || 0;
      }
    },

    // Update Company Admin count
    updateCompanyAdminCount: (state, action) => {
      const { type } = action.payload;
      if (type === 'increment') {
        state.companyAdminCount += 1;
      } else if (type === 'decrement') {
        state.companyAdminCount = Math.max(0, state.companyAdminCount - 1);
      } else if (type === 'set') {
        state.companyAdminCount = action.payload.count || 0;
      }
    },

    // Reset both sidebar counts
    resetSidebarCounts: (state) => {
      state.superAdminCount = 0;
      state.companyAdminCount = 0;
    },

    // Set all unread counts at once (for global hook)
    setUnreadCounts: (state, action) => {
      const { total, threads, roleBased, superAdmin, companyAdmin } = action.payload;
      
      if (typeof total === 'number') {
        state.totalUnreadCount = total;
        state.totalCount = total;
      }
      
      if (threads && typeof threads === 'object') {
        state.threadCounts = threads;
      }
      
      if (roleBased && typeof roleBased === 'object') {
        state.roleBased = { ...state.roleBased, ...roleBased };
      }
      
      if (typeof superAdmin === 'number') {
        state.superAdminCount = superAdmin;
      }
      
      if (typeof companyAdmin === 'number') {
        state.companyAdminCount = companyAdmin;
      }
    },

    // Increment total count (for new message received)
    incrementTotalCount: (state) => {
      state.totalUnreadCount += 1;
      state.totalCount += 1;
    },

    // Decrement total count (for message read)
    decrementTotalCount: (state, action) => {
      const amount = action.payload || 1;
      state.totalUnreadCount = Math.max(0, state.totalUnreadCount - amount);
      state.totalCount = Math.max(0, state.totalCount - amount);
    },
  },
});

export const {
  updateCount,
  setCount,
  resetCount,
  setLoading,
  setError,
  initializeThreadCount,
  incrementThreadCount,
  decrementThreadCount,
  clearThreadCount,
  clearAllCounts,
  setThreadCount,
  updateMultipleThreadCounts,
  getThreadCount,
  updateSuperAdminCount,
  updateCompanyAdminCount,
  resetSidebarCounts,
  setUnreadCounts,
  incrementTotalCount,
  decrementTotalCount,
} = messageCountSlice.actions;

export default messageCountSlice.reducer;
